/**
 * Defines the {@link LoadingIndicator} functional component.
 * @module
 */
import React, { FC, ReactElement } from "react";
import { LoadingIndicatorTypes } from "@componentTypes";
import "./LoadingIndicator.scss";
import CircularProgress from "@mui/material/CircularProgress";

/**
 * Props interface for {@link LoadingIndicator}.
 */
interface Props {
    /**
     * The label that will be displayed along side the loading indicator.
     */
    label?: string;
    /**
     * If true, the loading indicator is the light color.
     */
    isLightColor?: boolean;
    /**
     * The size of the loading indicator label.
     * If no value is provided, {@link LoadingIndicatorTypes.LabelSize.MEDIUM} will be used.
     */
    labelSize?: LoadingIndicatorTypes.LabelSize;
    /**
     * The size of the loading indicator.
     * If no value is provided, {@link LoadingIndicatorTypes.IndicatorSize.MEDIUM} will be used.
     */
    indicatorSize?: LoadingIndicatorTypes.IndicatorSize | number;
}

/**
 * Generates a loading indicator.
 * @param props - The component props.
 * @returns the LoadingIndicator component.
 */
export const LoadingIndicator: FC<Props> = (props: Props): ReactElement => (
    <div className="LoadingIndicator">
        {props.label ? (
            <label
                aria-label={props.label}
                className={`loading-indicator-label ${
                    props.labelSize ? props.labelSize : LoadingIndicatorTypes.LabelSize.MEDIUM
                }`}
            >
                {props.label}
            </label>
        ) : undefined}
        <CircularProgress
            aria-labelledby={props.label}
            className={`loading-indicator ${props.isLightColor ? "light-color" : "dark-color"}`}
            size={props.indicatorSize || LoadingIndicatorTypes.IndicatorSize.MEDIUM}
        />
    </div>
);
